import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaMarkup from '@/components/feature/SchemaMarkup';
import { getArticleBySlug, articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-600 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '使用教程', path: '/category/guide' },
    { name: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    url: `${siteInfo.url}/article/${article.slug}`,
  };

  return (
    <Layout>
      <SEOMeta
        title={`${article.title} - ${siteInfo.name}`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${siteInfo.url}/article/${article.slug}`}
        ogType="article"
      />
      <SchemaMarkup schema={articleSchema} />

      <article className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb items={breadcrumbItems} />

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)] mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line text-sm"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line text-sm"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line text-sm"></i>
              {article.readTime} 阅读
            </span>
          </div>
        </div>

        <div className="mb-8 rounded-xl overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-64 md:h-80 object-cover object-top"
          />
        </div>

        <div
          className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
            prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-8 prose-h2:mb-4
            prose-h3:text-lg prose-h3:font-semibold prose-h3:text-foreground-950 prose-h3:mt-6 prose-h3:mb-3
            prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
            prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-4
            prose-ol:list-decimal prose-ol:pl-5 prose-ol:mb-4
            prose-li:text-foreground-700 prose-li:mb-1
            prose-strong:text-foreground-950"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {relatedArticles.length > 0 && (
          <div className="mt-14 pt-10 border-t border-background-200/70">
            <h2 className="text-lg font-bold text-foreground-950 font-[var(--font-heading)] mb-5">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/article/${rel.slug}`}
                  className="group p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                >
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                    {rel.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{rel.excerpt}</p>
                  <span className="text-xs text-foreground-400">{rel.date}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </Layout>
  );
}